/**
 * Onboarding slice for Redux store
 */

import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { createCheckoutSession } from '../services/billingService';
import { BillingCycle } from '../types/billing';
import { SubscriptionPackage } from '../types/company';
import {
  OnboardingFormData,
  OnboardingState,
  OnboardingStep,
} from '../types/onboarding';

const initialState: OnboardingState = {
  currentStep: OnboardingStep.PERSONAL_INFO,
  formData: {},
  isLoading: false,
  error: null,
  isComplete: false,
};

/**
 * Thunk: Complete payment setup
 */
export const completePaymentSetup = createAsyncThunk(
  'onboarding/completePaymentSetup',
  async (
    {
      billingCycle,
      packageId,
      companyId,
    }: {
      billingCycle?: BillingCycle;
      packageId: SubscriptionPackage;
      companyId: number;
    },
    { rejectWithValue },
  ) => {
    const response = await createCheckoutSession(
      packageId,
      companyId,
      billingCycle,
    );
    if (!response.success || !response.sessionId || !response.sessionUrl) {
      return rejectWithValue(
        response.error || 'Failed to create checkout session',
      );
    }
    return {
      sessionId: response.sessionId,
      sessionUrl: response.sessionUrl,
    };
  },
);

const onboardingSlice = createSlice({
  name: 'onboarding',
  initialState,
  reducers: {
    setCurrentStep: (state, action: PayloadAction<OnboardingStep>) => {
      state.currentStep = action.payload;
    },
    updateFormData: (
      state,
      action: PayloadAction<Partial<OnboardingFormData>>,
    ) => {
      state.formData = { ...state.formData, ...action.payload };
    },
    nextStep: (state) => {
      if (state.currentStep < OnboardingStep.COMPLETE) {
        state.currentStep += 1;
      }
    },
    previousStep: (state) => {
      if (state.currentStep > OnboardingStep.PERSONAL_INFO) {
        state.currentStep -= 1;
      }
    },
    resetOnboarding: () => initialState,
    completeOnboarding: (state) => {
      state.isComplete = true;
      state.currentStep = OnboardingStep.COMPLETE;
    },
    setPendingUserId: (state, action: PayloadAction<string>) => {
      state.pendingUserId = action.payload;
    },
    clearError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    // Payment setup
    builder.addCase(completePaymentSetup.pending, (state) => {
      state.isLoading = true;
      state.error = null;
    });
    builder.addCase(completePaymentSetup.fulfilled, (state) => {
      state.isLoading = false;
    });
    builder.addCase(completePaymentSetup.rejected, (state, action) => {
      state.isLoading = false;
      state.error = action.payload as string;
    });
  },
});

export const {
  setCurrentStep,
  updateFormData,
  nextStep,
  previousStep,
  resetOnboarding,
  completeOnboarding,
  setPendingUserId,
  clearError,
} = onboardingSlice.actions;

export default onboardingSlice.reducer;
